/**
 * BuildStamp — admin-only build/version + cache diagnostics.
 *
 * Shows the release label, the bundle hash this device is RUNNING, and the
 * hash the server currently advertises. If they differ, the device is on a
 * stale cache → an inline Reload is offered. Exposes only build identifiers,
 * never secrets/env vars.
 */
import React, { useState } from "react";
import { Package, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { useBuildVersion } from "../hooks/useBuildVersion";

export const BuildStamp = () => {
  const { release, loadedHash, latestHash, updateAvailable, reload } = useBuildVersion();
  const [reloading, setReloading] = useState(false);

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground border border-border bg-secondary/30 px-3 py-2"
      data-testid="build-stamp"
    >
      <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
        <Package className="w-3.5 h-3.5" /> Build
      </span>
      <span data-testid="build-release">release <span className="font-mono-tab text-foreground">{release}</span></span>
      <span data-testid="build-bundle">bundle <span className="font-mono-tab text-foreground">{loadedHash || "dev"}</span></span>
      {updateAvailable ? (
        <>
          <span className="inline-flex items-center gap-1 text-[#92400E]" data-testid="build-status-stale">
            <AlertTriangle className="w-3.5 h-3.5" /> update available (server <span className="font-mono-tab">{latestHash}</span>)
          </span>
          <button
            onClick={async () => { setReloading(true); await reload(); }}
            disabled={reloading}
            className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white font-bold transition-colors disabled:opacity-60"
            data-testid="build-reload"
          >
            <RefreshCw className={`w-3 h-3 ${reloading ? "animate-spin" : ""}`} /> Reload
          </button>
        </>
      ) : (
        <span className="inline-flex items-center gap-1 text-[#065F46]" data-testid="build-status-current">
          <CheckCircle2 className="w-3.5 h-3.5" /> up to date
        </span>
      )}
    </div>
  );
};

export default BuildStamp;
